<script setup>
import { computed } from 'vue'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'

const props = defineProps({
  modelValue: { type: Object, default: null },
  attributes: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  loadError: { type: String, default: null },
  validationMessage: { type: String, default: null },
  disabled: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue', 'retry-attributes'])

const PART_TYPE = Object.freeze({ ModelCode: 0, AttributeValueCode: 1, Literal: 2 })
const allPartTypes = [
  { label: 'ModelCode', value: PART_TYPE.ModelCode },
  { label: 'Характеристика', value: PART_TYPE.AttributeValueCode },
  { label: 'Постоянный текст', value: PART_TYPE.Literal }
]
const parts = computed(() => Array.isArray(props.modelValue?.parts) ? props.modelValue.parts : [])
const hasModelCode = computed(() => parts.value.some(part => part.type === PART_TYPE.ModelCode))
const partTypeOptions = computed(() => hasModelCode.value
  ? allPartTypes.filter(option => option.value !== PART_TYPE.ModelCode)
  : allPartTypes)
const normalizedAttributes = computed(() => props.attributes.map(attribute => ({
  id: attribute.id ?? attribute.Id ?? attribute.productAttributeId,
  name: attribute.name ?? attribute.Name
})))
const previewParts = computed(() => parts.value.map(part => {
  if (part.type === PART_TYPE.ModelCode) return 'ModelCode'
  if (part.type === PART_TYPE.Literal) return `«${part.literal ?? ''}»`
  const attributeId = part.productAttributeId
  const attribute = normalizedAttributes.value.find(item => Number(item.id) === Number(attributeId))
  return attribute ? attribute.name : `Характеристика #${attributeId} недоступна`
}))

function publish(nextParts) {
  emit('update:modelValue', { parts: nextParts })
}
function createTemplate() {
  publish([{ type: PART_TYPE.ModelCode, productAttributeId: null, literal: null }])
}
function removeTemplate() {
  emit('update:modelValue', null)
}
function addPart(type = PART_TYPE.AttributeValueCode) {
  if (type === PART_TYPE.ModelCode && hasModelCode.value) return
  const part = type === PART_TYPE.AttributeValueCode
    ? { type, productAttributeId: null, literal: null }
    : type === PART_TYPE.Literal
      ? { type, productAttributeId: null, literal: '' }
      : { type: PART_TYPE.ModelCode, productAttributeId: null, literal: null }
  publish([...parts.value, part])
}
function changePartType(index, type) {
  if (type === PART_TYPE.ModelCode && hasModelCode.value && parts.value[index]?.type !== PART_TYPE.ModelCode) return
  const part = type === PART_TYPE.AttributeValueCode
    ? { type, productAttributeId: null, literal: null }
    : type === PART_TYPE.Literal
      ? { type, productAttributeId: null, literal: '' }
      : { type: PART_TYPE.ModelCode, productAttributeId: null, literal: null }
  publish(parts.value.map((item, itemIndex) => itemIndex === index ? part : item))
}
function setPartValue(index, field, value) {
  publish(parts.value.map((part, itemIndex) => itemIndex === index ? { ...part, [field]: value } : part))
}
function removePart(index) {
  publish(parts.value.filter((_, itemIndex) => itemIndex !== index))
}
function movePart(index, direction) {
  const target = index + direction
  if (target < 0 || target >= parts.value.length) return
  const next = [...parts.value]
  ;[next[index], next[target]] = [next[target], next[index]]
  publish(next)
}
function attributeOptionsFor(part) {
  const options = normalizedAttributes.value.map(attribute => ({ label: attribute.name, value: attribute.id }))
  const id = part.productAttributeId
  if (id != null && !options.some(option => Number(option.value) === Number(id))) {
    options.unshift({ label: `Характеристика #${id} недоступна`, value: id, disabled: true })
  }
  return options
}

defineExpose({ createTemplate, removeTemplate, addPart, changePartType, setPartValue, removePart, movePart,
  partTypeOptions, previewParts, hasModelCode, attributeOptionsFor })
</script>

<template>
  <section class="article-template-editor">
    <div class="article-template-heading">
      <div>
        <h3>Структура артикула</h3>
        <p class="hint">Опишите порядок частей Article. Сопоставление характеристик со значениями здесь не выполняется.</p>
      </div>
      <Button v-if="!modelValue" label="Настроить структуру артикула" icon="pi pi-plus" outlined
        :disabled="disabled" @click="createTemplate" />
      <Button v-else label="Удалить шаблон" icon="pi pi-trash" severity="secondary" text
        :disabled="disabled" @click="removeTemplate" />
    </div>

    <template v-if="modelValue">
      <div v-if="loading" class="hint">Загружаем характеристики…</div>
      <div v-if="loadError" class="article-template-error">
        {{ loadError }}
        <Button label="Повторить загрузку" text :disabled="disabled" @click="emit('retry-attributes')" />
      </div>
      <div v-for="(part, index) in parts" :key="index" class="article-template-row">
        <span class="article-template-order">{{ index + 1 }}.</span>
        <Dropdown :model-value="part.type" :options="partTypeOptions" option-label="label" option-value="value"
          :disabled="disabled" aria-label="Тип части Article" @update:model-value="changePartType(index, $event)" />

        <Dropdown v-if="part.type === PART_TYPE.AttributeValueCode" :model-value="part.productAttributeId"
          :options="attributeOptionsFor(part)" option-label="label" option-value="value" placeholder="Выберите характеристику"
          :disabled="disabled || loading" aria-label="Характеристика"
          @update:model-value="setPartValue(index, 'productAttributeId', $event)" />
        <InputText v-else-if="part.type === PART_TYPE.Literal" :model-value="part.literal" placeholder="Постоянный текст"
          :disabled="disabled" aria-label="Постоянный текст" @update:model-value="setPartValue(index, 'literal', $event)" />
        <span v-else class="article-template-model-code">ModelCode вычисляется в отдельной настройке</span>

        <div class="article-template-order-actions">
          <Button icon="pi pi-arrow-up" text rounded aria-label="Переместить выше"
            :disabled="disabled || index === 0" @click="movePart(index, -1)" />
          <Button icon="pi pi-arrow-down" text rounded aria-label="Переместить ниже"
            :disabled="disabled || index === parts.length - 1" @click="movePart(index, 1)" />
          <Button icon="pi pi-times" text rounded severity="danger" aria-label="Удалить часть"
            :disabled="disabled" @click="removePart(index)" />
        </div>
      </div>

      <Button label="Добавить часть" icon="pi pi-plus" text :disabled="disabled" @click="addPart()" />
      <div v-if="validationMessage" class="article-template-error">{{ validationMessage }}</div>
      <div v-if="parts.length" class="article-template-preview">
        <strong>Представление шаблона:</strong>
        <span>{{ previewParts.join(' → ') }}</span>
      </div>
    </template>
  </section>
</template>

<style scoped>
.article-template-editor { margin: 0 0 16px; padding: 16px; border: 1px solid #e5e5e5; border-radius: 10px; }
.article-template-heading { display: flex; justify-content: space-between; align-items: start; gap: 12px; flex-wrap: wrap; }
.article-template-heading h3 { margin: 0 0 6px; }
.article-template-heading p { margin: 0 0 12px; }
.article-template-row { display: grid; grid-template-columns: 30px minmax(170px, 220px) minmax(200px, 1fr) auto; align-items: center; gap: 10px; margin: 10px 0; }
.article-template-order { color: #666; }
.article-template-model-code { color: #666; font-size: 12px; }
.article-template-order-actions { display: flex; gap: 2px; }
.article-template-preview { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; padding: 10px; background: #f7f7f5; border-radius: 8px; }
.article-template-error { color: #b42318; font-size: 13px; margin-top: 8px; }
.hint { color: #777; font-size: 12px; }
@media (max-width: 760px) { .article-template-row { grid-template-columns: 24px 1fr; } .article-template-row > :nth-child(3) { grid-column: 2; } .article-template-order-actions { grid-column: 2; justify-content: end; } }
</style>
